/**
 * 工具函数集
 * @author chenqunfeng, VenDream
 * @since 2018-7-11
 */

import store from 'store';
import Stat from '@fe/common/lib/stat';
import VARIABLES from 'constants/variable.json5';
import * as qs from '@fe/common/lib/query-string';
import { isInEditorIframe } from 'utils/env';

const inEditorIframe = isInEditorIframe();

/**
 * 拼接查询参数
 *
 * @param {*} link 链接
 * @returns
 */
export function getUrlWithQuery(link) {
  if (!link) {
    return link;
  }

  const { extParams, extSource } = qs.parse(window.location.search);

  // 营销活动内的作品跳转到编辑器都会需携带URL参数用于区分来源，编辑器拿到这些参数用于判断是否为营销活动内的作品
  // 携带参数方案：
  // 1. 增加来源枚举类型 extSource=1，1 表示营销活动
  // 2. 增加营销活动 ID 参数 extParams = activityId，activityId 表示营销活动 ID
  if (extSource && extParams) {
    // 营销活动内容参数
    const extStr = qs.stringify({ extSource, extParams });
    // 分隔符
    const separator = link.includes('?') ? '&' : '?';

    return [link, extStr].join(separator);
  }

  return link;
}

// 去掉对象的空属性
export function cleanObj(obj) {
  const newObj = Object.assign({}, obj);

  Object.keys(newObj).forEach(
    key =>
      (newObj[key] === null || newObj[key] === undefined) && delete newObj[key]
  );

  return newObj;
}

/**
 * 上报统计数据
 * @param {String} name        上报名称
 * @param {String} cmpclass    上报组件类
 * @param {String} cmpvalue    上报组件值
 * @param {String} action      上报类型
 * @param {Object} [others={}] 上报附加数据
 */
export function sendStat(name, cmpclass, cmpvalue, action, others = {}) {
  const user = store.getState().userInfo;
  const { userId, userHid } = user;
  // 附加用户数据
  others = {
    ...others,
    user_id: userHid || '',
    rp_user_id: userId || '',
  };

  return Stat.sendComponent(name, cmpclass, cmpvalue, action, others);
}

// 获取当前URL的pathname值
export function getUrlPathname() {
  return window.location.pathname;
}

/**
 * 获取编辑器URL
 *
 * @export
 * @param {any} tplId 模板id
 * @param {any} type 模版类型
 * @param {any} category 模板分类
 * @param {any} videoHost 视频host
 * @returns
 */
export function getEditorUrl(tplId = '', type, category, videoHost = '') {
  let goUrl = '';

  if (!type) return;

  // 视频模版
  if (videoHost) {
    goUrl = `${videoHost.replace(
      /^http:\/\//,
      location.protocol + '//'
    )}?templateid=${tplId}`;
    return goUrl;
  }

  // 新建作品要根据选择的模板类型来决定内容类型
  // 目前展示页、活动玩法、推荐页有新建空白模板可以点击
  const contentTypeMap = {
    [VARIABLES.SHOWPAGE]: VARIABLES.CONTENT_TYPE.SHOW_PAGE,
    [VARIABLES.CHAJIAN]: VARIABLES.CONTENT_TYPE.ACTIVITY_PAGE,
    [VARIABLES.POSTER]: VARIABLES.CONTENT_TYPE.POSTER,
  };
  const contentType = contentTypeMap[type] || 'showPage';

  const editorHost = window.RPCONFIG.editorHost;
  goUrl = tplId
    ? category === VARIABLES.POSTER || type === VARIABLES.POSTER
      ? `${editorHost}?contentType=${contentType}&templateid=${tplId}&type=${type}&category=${category}`
      : `${editorHost}?templateid=${tplId}&type=${type}&category=${category}`
    : `${editorHost}?contentType=${contentType}`;
  return goUrl;
}

const iframeEditor = window.RPCONFIG.iframeEditor;

/**
 * 跳转编辑器
 *
 * @export
 * @param {string} tplId 模版id
 * @param {string} type 模版类型
 * @param {string} category 模版分类
 * @param {string} videoHost 视频host
 */
export function gotoEditor(tplId, type, category, videoHost) {
  const state = store.getState();
  const user = state.userInfo;
  const {
    isPostMessageInIframe: opMessage = 0,
    isRedirectInIframe: opRedirect = 0,
  } = user.platformConfigInfo || {};

  const editorUrl = getUrlWithQuery(
    getEditorUrl(tplId, type, category, videoHost)
  );

  const message = {
    type: 'LINK_TARGET', //消息类型（页面跳转）
    url: editorUrl, // 目标页面URL
    fullscreen: true, // 是否全屏显示页面（有此属性且等于true则全屏显示，否则就取消全屏）
  };
  /**
   * @note 有赞（有kdtid）并且
   */
  if (user.isYouzan) {
    window.open(editorUrl);
  } else if (inEditorIframe) {
    /**
     * @note 在编辑器中通过iframe打开模板市场，
     *       添加特殊参数判断 inEditorIframe=1，需要用 postMessage 和外部编辑器通讯
     *       优先级比开放平台 opMessage || opRedirect 高
     * @date 2021-5-24
     */
    const data = {
      type: 'STORE_IN_EDITOR', //消息类型 模板市场被嵌套在编辑器中
      search: { templateid: tplId, type, category },
    };
    console.debug('post message: ', data);
    window.parent.postMessage(data, '*');
  } else if (window.top !== window && (opMessage === 1 || opRedirect === 1)) {
    // 开放平台的配置优先级比环境配置高
    if (opMessage === 1) {
      console.debug('post message: ', message);
      window.parent.postMessage(JSON.stringify(message), '*');
    }
    if (opRedirect === 1) {
      window.location.href = editorUrl;
    }
  } else if (iframeEditor && iframeEditor.third) {
    if (iframeEditor.third === 'tencent-cloud') {
      console.debug('post message: ', message);
      window.parent.postMessage(JSON.stringify(message), '*');
      window.location.href = editorUrl;
    }
  } else {
    window.location.href = editorUrl;
  }
  return;
}

/**
 * 跳转海报编辑器
 *
 * @export
 * @param {string} width 海报size-宽
 * @param {string} height 海报size-高
 */
export function gotoPosterEditor(width, height) {
  const editorHost = window.RPCONFIG.editorHost;
  const goUrl = `${editorHost}?contentType=${VARIABLES.CONTENT_TYPE.POSTER}&width=${width}&height=${height}`;
  window.location.href = getUrlWithQuery(goUrl);
}

/**
 * 跳转游戏编辑器
 *
 * @export
 * @param {string} tplId 模版id
 * @param {string} type 模版类型
 * @param {string} category 模版分类
 * @param {string} videoHost 视频host
 */
export function gotoGameEditor(id) {
  const host = RPCONFIG.gameEditorHost;
  let gameEditorUrl = `${host}/editor.html?appid=${id}`;
  window.location.replace(gameEditorUrl);
  return;
}

/**
 * 跳转-模板搜索页
 * @param {string} filterKey 筛选项字段
 * @param {string} filterValue 筛选项值
 * @param {string} filterValue 筛选项名称
 */
export function gotoSearchPage(obj) {
  const filters = obj;
  window.open(
    `/template/search.html?filters=${encodeURIComponent(
      JSON.stringify(filters)
    )}`
  );
  return;
}

/**
 * 启用HMR热重载
 *
 * @export
 * @param {*} module 模块
 */
export function enableHMR(module) {
  if (process.env.NODE_ENV === 'development') {
    const HMR = module.hot;
    HMR && HMR.accept && HMR.accept();
  }
}

/**
 * 修改url字段值
 */
export function changeUrlArg(url, arg, val) {
  var pattern = arg + '=([^&]*)';
  var replaceText = arg + '=' + val;
  return url.match(pattern)
    ? url.replace(eval('/(' + arg + '=)([^&]*)/gi'), replaceText)
    : url.match('[?]')
    ? url + '&' + replaceText
    : url + '?' + replaceText;
}

/**
 * 更新搜索页路由 参数
 * @param {string} key
 * @param {string} value
 */
export function updateSearchUrlKey(key, value) {
  const newUrl = changeUrlArg(window.location.href, key, value);
  window.history.pushState(null, '', newUrl);
}

/**
 * 更新搜索页路由 筛选项参数
 * @param {string} key
 * @param {string} value
 */
export function updateSearchUrlFilterData(key, value) {
  const queryObj = qs.parse(window.location.search) || {};
  const { filters } = queryObj;
  let filtersList;
  try {
    filtersList = JSON.parse(decodeURIComponent(filters));
  } catch (e) {
    filtersList = {};
  }
  if (value !== 'ALL') {
    filtersList[key] = value;
  } else {
    delete filtersList[key];
  }

  updateSearchUrlKey(
    'filters',
    encodeURIComponent(JSON.stringify(filtersList))
  );
}

/**
 * 确保一个函数仅被调用一次。
 * @param fn
 */
export const once = fn => {
  let result;
  return function(...args) {
    if (!fn) return;
    result = fn.apply(this, args);
    fn = null;
    return result;
  };
};

/**
 * 生成固定长度的随机字符串
 * @param {number} n
 * @return 返回固定长度的随机字符串
 */
export const uniqueId = (n = 20) => {
  let result = '';
  while (result.length < n) {
    result += Math.random()
      .toString(36)
      .substr(2);
  }

  return result.substr(0, n);
};
